/**
 * Extend Contract Dialog
 * Allows requesting a contract extension on a Matured contract.
 * Pushes the maturity date forward on the same contract (no new contract created).
 * Creates a pending_changes entry for approval workflow.
 * Preview opens ScheduleImpactPreviewModal with projected schedule.
 */

import React, { useState, useEffect } from 'react';
import { TextField, Grid } from '@mui/material';
import { format, addMonths, parseISO } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';
import ScheduleImpactPreviewModal from './ScheduleImpactPreviewModal';

const ExtendContractDialog = ({ open, onClose, contract, onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    extension_months: '',
    new_maturity_date: '',
    new_interest_rate: '',
  });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (open && contract) {
      setFormData({
        extension_months: '',
        new_maturity_date: '',
        new_interest_rate: contract.interest_rate || '',
      });
      setReason('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
    }
  }, [open, contract]);

  // Auto-calculate new maturity date
  useEffect(() => {
    if (formData.extension_months && contract?.maturity_date) {
      try {
        const currentMaturity = parseISO(contract.maturity_date.substring(0, 10));
        const newMaturity = addMonths(currentMaturity, parseInt(formData.extension_months));
        setFormData(prev => ({ ...prev, new_maturity_date: format(newMaturity, 'yyyy-MM-dd') }));
        setPreviewData(null);
      } catch { /* invalid date */ }
    }
  }, [formData.extension_months, contract?.maturity_date]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'new_interest_rate') setPreviewData(null);
  };

  const handlePreview = async () => {
    if (!formData.new_maturity_date) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ new_maturity_date: formData.new_maturity_date });
      if (formData.new_interest_rate) params.append('new_interest_rate', formData.new_interest_rate);
      const response = await apiGet(`/amendments/contract/${contract.id}/extension-preview?${params}`);
      if (response?.success) {
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      }
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${contract.id}/extend`, {
        extension_months: parseInt(formData.extension_months),
        new_maturity_date: formData.new_maturity_date,
        new_interest_rate: formData.new_interest_rate ? parseFloat(formData.new_interest_rate) : null,
        reason,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSubmitSuccess) onSubmitSuccess(response.data);
      } else {
        setError(response?.message || 'Failed to submit extension request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit extension request');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = formData.extension_months && parseInt(formData.extension_months) > 0 && formData.new_maturity_date;

  return (
    <>
      <ContractActionDialogBase
        open={open}
        onClose={onClose}
        contract={contract}
        workflowCode="contract_extend"
        title="Extend Contract"
        description="Extending a contract pushes the maturity date forward on the same contract. Capital remains invested. A new schedule for the extension period will be generated upon approval."
        submitLabel="Submit for Approval"
        submitColor="primary"
        isValid={isValid}
        submitting={submitting}
        error={error}
        onClearError={() => setError(null)}
        onSubmit={handleSubmit}
        onPreview={handlePreview}
        showPreview={true}
        previewLoading={previewLoading}
        reason={reason}
        onReasonChange={setReason}
        reasonLabel="Reason (Required)"
        reasonPlaceholder="Provide a reason for the extension. This will be reviewed by an approver."
        maxWidth="lg"
      >
        <Grid container spacing={2}>
          <Grid size={4}>
            <TextField
              label="Extension Term (months)"
              type="number"
              value={formData.extension_months}
              onChange={(e) => handleFieldChange('extension_months', e.target.value)}
              fullWidth
              required
              inputProps={{ min: 1 }}
              helperText="Number of months to extend"
            />
          </Grid>
          <Grid size={4}>
            <TextField
              label="New Maturity Date"
              type="date"
              value={formData.new_maturity_date}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              helperText="Auto-calculated from extension term"
            />
          </Grid>
          <Grid size={4}>
            <TextField
              label="Interest Rate (%)"
              type="number"
              value={formData.new_interest_rate}
              onChange={(e) => handleFieldChange('new_interest_rate', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.0001' }}
              helperText="Leave unchanged to keep current rate"
            />
          </Grid>
        </Grid>
      </ContractActionDialogBase>
      <ScheduleImpactPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Extension Impact Preview"
        subtitle={`Extending maturity to ${formData.new_maturity_date}${formData.new_interest_rate !== String(contract?.interest_rate) ? ` at ${formData.new_interest_rate}%` : ''}`}
        previewData={previewData}
        contract={contract}
        contractOverrides={{
          ...(formData.new_maturity_date ? { maturity_date: formData.new_maturity_date } : {}),
          ...(formData.new_interest_rate && formData.new_interest_rate !== String(contract?.interest_rate)
            ? { interest_rate: parseFloat(formData.new_interest_rate) }
            : {}),
        }}
      />
    </>
  );
};

export default ExtendContractDialog;
